import { Box, Chip, IconButton, Tooltip, Typography } from '@mui/material'
import { useState } from 'react'
import { DataGrid } from '@mui/x-data-grid'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useBasePath } from 'components/Providers'
import NoRecords from 'components/NoRecords'
import { capitalize } from 'lodash'
import { EditIcon, DeleteIcon } from 'components/Icons'
import { destroy } from 'store/settings/service-access'
import Confirm from 'components/Dialog'

function RoleCell({ roles }) {
  if (!roles?.length) return null
  const getName = r => r.role?.name ?? r.role
  const unique = roles.reduce((acc, r) => {
    const name = getName(r)
    if (!acc.find(a => getName(a) === name)) acc.push(r)
    return acc
  }, [])
  const [first, ...rest] = unique

  return (
    <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', py: 0.5 }}>
      <Typography variant='body2'>{getName(first)}</Typography>
      {rest.length > 0 && (
        <Tooltip title={rest.map(getName).join(', ')} arrow>
          <Typography variant='caption'>{`+${rest.length}`}</Typography>
        </Tooltip>
      )}
    </Box>
  )
}

export default function SchoolAccessList({ list, loading }) {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const basePath = useBasePath()
  const campusList = useSelector(state => state.lookup.campus)
  const [pendingDeleteId, setPendingDeleteId] = useState(null)

  const columns = [
    {
      field: 'person',
      headerName: t('Name'),
      flex: 1,
      renderCell: ({ row }) => `${row?.lastName ?? ''}, ${row?.firstName ?? ''}`,
    },
    {
      field: 'email',
      headerName: t('Email'),
      flex: 1,
      renderCell: ({ row }) => row.email,
    },
    {
      field: 'role',
      headerName: t('User Role'),
      flex: 1,
      renderCell: ({ row }) => <RoleCell roles={row.personServiceRoles} />,
    },
    {
      field: 'campus',
      headerName: t('Campus'),
      flex: 1,
      renderCell: ({ row }) => {
        const roles = row.personServiceRoles ?? []
        const hasFullAccess = roles.some(r => !r.campusId?.length)
        const uniqueCampusIds = [...new Set(roles.flatMap(r => r.campusId ?? []))]
        const type = roles[0]?.type
        return (
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', alignItems: 'center', py: 0.5 }}>
            {hasFullAccess
              ? <Chip size='small' variant='outlined' color='success' label={`Full ${capitalize(type)}`} />
              : uniqueCampusIds.map(id => (
                <Chip
                  key={id}
                  size='small'
                  variant='outlined'
                  color='info'
                  label={campusList?.find(c => c.id === id)?.name ?? id}
                />
              ))
            }
          </Box>
        )
      },
    },
    {
      field: 'actions',
      headerName: t('Action'),
      width: 88,
      sortable: false,
      renderCell: ({ row }) => (
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <Tooltip title={t('Edit')} arrow>
            <IconButton
              size='small'
              onClick={e => { e.stopPropagation(); navigate(`${basePath}/${row.id}`) }}
            >
              <EditIcon fontSize='small' />
            </IconButton>
          </Tooltip>
          <Tooltip title={t('Remove Access')} arrow>
            <IconButton
              size='small'
              onClick={e => { e.stopPropagation(); setPendingDeleteId(row?.personServiceRoles[0]?.id) }}
            >
              <DeleteIcon fontSize='small' />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ]

  return (
    <>
      <DataGrid
        rows={list ?? []}
        columns={columns}
        getRowId={row => row.id}
        autoHeight
        hideFooter
        disableColumnSorting
        disableColumnMenu
        slots={{ noRowsOverlay: NoRecords }}
        loading={loading}
        rowHeight={56}
        sx={{ '& .MuiDataGrid-cell': { display: 'flex', alignItems: 'center' } }}
      />
      <Confirm
        open={Boolean(pendingDeleteId)}
        onClose={() => setPendingDeleteId(null)}
        dialogTitle={t('Confirm Remove Access')}
        message={t('Are you sure you want to remove this access?')}
        onConfirm={() => {
          dispatch(destroy({ id: pendingDeleteId }))
          setPendingDeleteId(null)
        }}
      />
    </>
  )
}
